import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { AlertMessages } from '../AlertMessages';
import { createManualTopup, getBalance } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatCoins';
import { FormContainer } from './styles';

const MIN_CODE_LENGTH = 3;

export const TopupForm = () => {
  const [studentCode, setStudentCode] = useState('');
  const [amountCoins, setAmountCoins] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [studentError, setStudentError] = useState(null);
  const debounceRef = useRef(null);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const getCurrentBranch = () => {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (savedBranch === 'ALTO_BRANCO' || savedBranch === 'CONCEICAO') {
        return savedBranch;
      }
    } catch (error) {
      console.error('[TopupForm] Erro ao ler filial do localStorage:', error);
    }
    return 'ALTO_BRANCO';
  };
  const [branch, setBranch] = useState(getCurrentBranch());

  // Buscar nome e saldo ao informar o código do estudante (com debounce)
  useEffect(() => {
    const code = studentCode.trim().toUpperCase();
    if (code.length < MIN_CODE_LENGTH) {
      setStudentInfo(null);
      setStudentError(null);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      setStudentError(null);
      setLoadingStudent(true);
      try {
        const data = await getBalance(code);
        setStudentInfo({ name: data.studentName, balance: data.balance });
      } catch (err) {
        const msg = err.response?.status === 404 ? 'Estudante não encontrado' : getErrorMessage(err);
        setStudentError(msg);
        setStudentInfo(null);
      } finally {
        setLoadingStudent(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [studentCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studentCode.trim() || !amountCoins) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preencha código do estudante e valor',
      });
      return;
    }

    const amount = Number(amountCoins.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Valor deve ser um número positivo',
      });
      return;
    }

    try {
      setLoading(true);
      const result = await createManualTopup({
        studentCode: studentCode.trim().toUpperCase(),
        amountCoins: amount,
        branch,
      });

      setAlertState({
        show: true,
        type: 'success',
        message: `Recarga realizada! Novo saldo: ${formatCurrency(result.balance)}`,
      });

      // Limpar formulário e dados do estudante
      setStudentCode('');
      setAmountCoins('');
      setStudentInfo(null);
      setStudentError(null);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormContainer>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="topup-branch-label">Filial</InputLabel>
            <Select
              labelId="topup-branch-label"
              id="topup-branch"
              value={branch}
              label="Filial"
              onChange={(e) => {
                const newBranch = e.target.value;
                if (newBranch === 'ALTO_BRANCO' || newBranch === 'CONCEICAO') {
                  setBranch(newBranch);
                  try {
                    localStorage.setItem('canteen_filial', newBranch);
                  } catch (error) {
                    console.error('[TopupForm] Erro ao salvar filial no localStorage:', error);
                  }
                }
              }}
            >
              <MenuItem value="ALTO_BRANCO">Alto Branco</MenuItem>
              <MenuItem value="CONCEICAO">Conceição</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Código do Estudante"
            value={studentCode}
            onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
            fullWidth
            required
            variant="outlined"
            placeholder="Ex: A1001 ou leia o cartão NFC"
          />

          {/* Dados do estudante (nome e saldo) após informar o código */}
          {studentCode.trim().length >= MIN_CODE_LENGTH && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: studentError ? '#ffebee' : studentInfo ? '#e8f5e9' : '#f5f5f5',
                border: '1px solid',
                borderColor: studentError ? 'error.light' : studentInfo ? 'success.light' : 'divider',
              }}
            >
              {loadingStudent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Buscando estudante...
                  </Typography>
                </Box>
              ) : studentError ? (
                <Typography variant="body2" color="error.main" fontWeight={500}>
                  {studentError}
                </Typography>
              ) : studentInfo ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonOutlineIcon sx={{ fontSize: 20, color: 'success.dark' }} />
                    <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                      {studentInfo.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: 'success.dark' }} />
                    <Typography variant="body2" color="text.secondary">
                      Saldo atual: <strong>{formatCurrency(studentInfo.balance)}</strong>
                    </Typography>
                  </Box>
                </Box>
              ) : null}
            </Paper>
          )}

          <TextField
            label="Valor (R$)"
            type="text"
            value={amountCoins}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*(?:[.,]\d{0,2})?$/.test(value)) {
                setAmountCoins(value);
              }
            }}
            fullWidth
            required
            variant="outlined"
            inputProps={{ inputMode: 'decimal' }}
            helperText="Valor em reais para adicionar (ex: 2.50)"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={
              loading ||
              (studentCode.trim().length >= MIN_CODE_LENGTH && (loadingStudent || studentError || !studentInfo))
            }
            sx={{
              bgcolor: '#1e958c',
              padding: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0b4f6c' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Realizar Recarga'}
          </Button>
        </Box>
      </FormContainer>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
